import React from "react";
import { Link } from "react-router-dom";
import CcgRoutePremiumPage from "../components/CcgRoutePremiumPage";
import PostcodeQuotenewMini from "./PostcodeQuotenewMini";
import "./Home.css";
import "./CcgRoutePremium.css";

const canonical = "https://www.cheapcabgrab.com/minibus-hire";

const faqItems = [
  {
    question: "How far in advance should we book?",
    answer:
      "For airports and busy weekends, we recommend booking as early as possible so we can secure the correct size minibus. Same-day requests may still be possible depending on availability.",
  },
  {
    question: "Can we book multiple pickups?",
    answer:
      "Yes. Add the details to your quote request, or contact our support team for complex multi-stop journeys and event shuttles.",
  },
  {
    question: "Are child seats and booster seats available?",
    answer:
      "Child and booster seats can be arranged when requested in advance. Tell us the ages of the children travelling so the correct seats can be prepared.",
  },
  {
    question: "Do you offer meet and greet at airports?",
    answer:
      "Meet and greet is available as an optional extra for minibus bookings. Standard pickups use a pre-agreed terminal or car park collection point.",
  },
  {
    question: "Can we use a minibus for corporate events?",
    answer:
      "Yes. Minibus hire is suitable for moving teams between offices, hotels, stations and venues. Our team can also help plan recurring routes and schedules.",
  },
  {
    question: "Where can I get help with a specific journey?",
    answer:
      "Use the quote form for a journey price, or contact CheapCabGrab if you need help with luggage, accessibility, multiple stops or vehicle capacity.",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "TaxiService",
      "@id": `${canonical}#service`,
      name: "CheapCabGrab – Minibus Hire",
      url: canonical,
      description:
        "Book 7–14 seater minibuses for Heathrow, Gatwick, Luton, Stansted and London. Fixed-price group transfers, licensed drivers and trusted PSV partners with CheapCabGrab.",
      areaServed: [
        "Heathrow Airport",
        "Gatwick Airport",
        "Luton Airport",
        "Stansted Airport",
        "London",
        "Slough",
        "Windsor",
        "Maidenhead",
      ].map((name) => ({ "@type": "Place", name })),
      serviceType: "Minibus hire for airport transfers and group travel",
      provider: {
        "@type": "Organization",
        name: "CheapCabGrab Ltd",
        url: "https://www.cheapcabgrab.com",
      },
      potentialAction: {
        "@type": "ReserveAction",
        target: canonical,
        result: { "@type": "Reservation", name: "Minibus hire quote" },
      },
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${canonical}#breadcrumb`,
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.cheapcabgrab.com",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Minibus Hire",
          item: canonical,
        },
      ],
    },
    {
      "@type": "FAQPage",
      "@id": `${canonical}#faq`,
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
  ],
};

const minibusHirePage = {
  id: "ccg-minibus-hire",
  path: "/minibus-hire",
  liveStripVariant: "heathrow",
  seo: {
    title:
      "Minibus Hire – 7–14 Seater Group Travel to Heathrow & London | CheapCabGrab",
    description:
      "Book 7–14 seater minibuses for Heathrow, Gatwick, Luton, Stansted and London. Fixed-price group transfers, licensed drivers and trusted PSV partners with CheapCabGrab.",
    canonical,
    keywords:
      "minibus hire, Heathrow minibus, Gatwick minibus, 9 seater minibus, 14 seater minibus, group airport transfers, CheapCabGrab",
    ogTitle: "Minibus Hire – 7–14 Seater Group Travel to Heathrow & London",
    ogDescription:
      "Reliable minibus hire for Heathrow, Gatwick, Luton, Stansted and London. 7–14 seater vehicles, luggage space, airport specialists and fixed quotes.",
    jsonLd,
  },
  hero: {
    eyebrow: "Airport, London and UK group travel",
    h1: "Minibus Hire for Heathrow, London & UK Trips",
    subtitle: "Group travel specialists for airports, events and UK tours",
    body:
      "Book reliable, luggage-ready minibuses for families, teams and groups with fixed-price quotes and licensed PSV partners.",
  },
  trustCards: [
    {
      icon: "✓",
      eyebrow: "GOV.UK registered operator",
      title: "Licensed & insured",
      body: "PSV licence PF2071924",
    },
    {
      icon: "G",
      eyebrow: "Google reviews",
      title: "4.8 ★★★★★",
      body: "View our customer reviews",
    },
  ],
  quote: {
    label: "Get an instant minibus quote",
    note: "Free • Fast • No obligation",
    pickupPlaceholder: "Enter pickup postcode or address",
    dropoffPlaceholder: "Enter drop-off postcode or address",
  },
  quotePromises: [
    "Fixed upfront quote",
    "Licensed drivers",
    "Luggage-ready vehicles",
    "Secure online booking",
  ],
  flags: [
    "Heathrow, Gatwick, Luton and Stansted coverage",
    "Minibuses for families, teams and groups",
    "No surge pricing or hidden fees",
  ],
  reviewPageKey: "/minibus-hire",
  reviewAriaLabel: "Minibus hire customer reviews",
  statusIntro:
    "Flying from Heathrow? Check live flight, terminal, queue, road, rail and weather signals before your group travels.",
  lhrStatusTitle: "Live Heathrow checks for minibus bookings",
  lhrStatusKicker: "Compact live LHR status",
  statusProfile: "minibus-hire",
  faqItems,
  floatingCta: "🚐 Get Minibus Quote",
  mobileCta: "Get Minibus Quote",
  supportNudge: "Need minibus help?",
  sections: [
    {
      id: "why-minibus",
      title: "Why Choose CheapCabGrab for Minibus Hire?",
      body:
        "A simple, professional minibus service for airport transfers, London trips and group travel.",
      render: () => (
        <div className="route-overview-grid">
          <article>
            <h3>Airport Specialists</h3>
            <p>
              We handle Heathrow, Gatwick, Luton and Stansted transfers for families,
              tour groups, crew travel and business teams.
            </p>
          </article>
          <article>
            <h3>Fixed, Upfront Pricing</h3>
            <p>
              No surge pricing or meter shock. Your quote reflects the route and vehicle
              required for your group before you confirm.
            </p>
          </article>
          <article>
            <h3>Licensed &amp; Insured</h3>
            <p>
              Licensed, insured vehicles and professional drivers are matched to early
              flights, late arrivals and group travel requirements.
            </p>
          </article>
        </div>
      ),
    },
    {
      id: "minibus-fleet",
      title: "Our Minibus Fleet – 7–14 Seater Options",
      body:
        "Choose the size that fits your group and luggage. Final allocation depends on availability and your booking details.",
      render: () => (
        <div className="route-areas-grid">
          <article>
            <h3>9 Seater Minibus</h3>
            <ul>
              <li>Up to 8 passengers</li>
              <li>Space for approximately 6–8 medium suitcases</li>
              <li>Suitable for families, small groups and airport transfers</li>
              <li>Available for day trips, attractions and local journeys</li>
            </ul>
          </article>
          <article>
            <h3>14 Seater Minibus</h3>
            <ul>
              <li>Up to 14 passengers</li>
              <li>Space for approximately 10–12 suitcases or mixed luggage</li>
              <li>Suitable for team travel, events and larger family groups</li>
              <li>Ideal for Heathrow, London venues and hotels</li>
            </ul>
          </article>
          <p className="route-faq-footer">
            For larger vehicles, visit our <Link to="/coach-hire">coach hire page</Link> or
            request a quote so we can match the right vehicle to your group.
          </p>
        </div>
      ),
    },
    {
      id: "minibus-routes",
      title: "Typical Minibus Routes & Fares (Guide)",
      body:
        "Exact prices depend on pickup area, date, time, group size and luggage. Use the quote form above for a journey price.",
      render: () => (
        <div className="route-overview-grid">
          <article>
            <h3>Heathrow Minibus Transfers</h3>
            <ul>
              <li>Slough, Windsor and Maidenhead to Heathrow</li>
              <li>Heathrow to Central London, West London and Docklands</li>
              <li>Group airport shuttles for hotels and conferences</li>
            </ul>
          </article>
          <article>
            <h3>Other London Airports</h3>
            <ul>
              <li>Gatwick transfers from Heathrow corridor towns</li>
              <li>Luton and Stansted group transfers</li>
              <li>Travel for tour groups and corporate teams</li>
            </ul>
          </article>
          <article>
            <h3>London &amp; Attractions</h3>
            <ul>
              <li>London city tours, shows and events</li>
              <li>LEGOLAND, Thorpe Park and other attractions</li>
              <li>Evening returns and multi-stop journeys</li>
            </ul>
          </article>
        </div>
      ),
    },
    {
      id: "minibus-partner",
      title: "Licensed PSV Partner Minibus Operations",
      body:
        "Where a PSV licence is required, CheapCabGrab works with trusted partners to provide safe, compliant group transport.",
      render: () => (
        <div className="route-support-box">
          <p>
            Certain minibus and coach services may be fulfilled by <strong>RHP MINIBUS
            HIRE UK LIMITED</strong> and other licensed PSV partners so that vehicles and
            drivers meet the relevant UK passenger transport requirements.
          </p>
          <p>
            You continue to receive your quote, booking information and support through
            CheapCabGrab while we match the correct licensed operator to your journey.
          </p>
        </div>
      ),
    },
    {
      id: "minibus-faq",
      title: "Minibus Hire FAQs",
      body: "Common questions about booking group transport with CheapCabGrab.",
      render: () => (
        <div className="route-faq-grid">
          {faqItems.map((item) => (
            <article className="route-faq-card" key={item.question}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </div>
      ),
    },
    {
      id: "minibus-links",
      title: "Useful Minibus and Airport Links",
      body: "Continue planning your group journey with our relevant services and airport tools.",
      render: () => (
        <div className="route-links-grid">
          {[
            ["Heathrow Minibus Hire", "/heathrow-minibus-hire"],
            ["Coach Hire", "/coach-hire"],
            ["Our Vehicles", "/fleet"],
            ["Our Drivers", "/our-drivers"],
            ["Heathrow Taxi Service", "/heathrow-taxi"],
            ["Slough Taxi", "/slough-taxi"],
            ["Windsor Taxi", "/windsor-taxi"],
            ["Maidenhead Taxi", "/maidenhead-taxi"],
            ["Terminal Finder", "/terminalFinder"],
          ].map(([label, to]) => (
            <article key={to}>
              <h3>{label}</h3>
              <Link className="route-link-btn" to={to}>View service</Link>
            </article>
          ))}
        </div>
      ),
    },
  ],
};

const MinibusHire = () => (
  <CcgRoutePremiumPage route={minibusHirePage} QuoteComponent={PostcodeQuotenewMini} />
);

export default MinibusHire;
